import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSave } from "react-icons/fa";

const HalfdayProvisionSelectBranches = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  function getFinYearId() {
    try {
      const direct = localStorage.getItem("finYearId");
      if (direct) return String(direct);
      const admin = JSON.parse(localStorage.getItem("admin") || "null");
      if (admin?.finYearId) return String(admin.finYearId);
    } catch (_) {}
    return "";
  }

  function tenantHeaders() {
    const fy = getFinYearId();
    return fy ? { "x-fin-year-id": fy } : {};
  }

  function tenantParams(extra = {}) {
    const fy = getFinYearId();
    return fy ? { ...extra, finYearId: fy } : extra;
  }

  const finYearId = useMemo(() => Number(getFinYearId() || 0), []);

  const axiosWithFY = useMemo(() => {
    return axios.create({
      baseURL: BASE_URL,
      headers: tenantHeaders(),
      params: tenantParams(),
    });
  }, [BASE_URL, finYearId]);

  // list + pagination
  const recordsPerPage = 20;
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // dirty changes: Map<branchId, enabled>
  const [dirtyMap, setDirtyMap] = useState(() => new Map());

  const fetchList = async (page = 1) => {
    if (!finYearId) {
      toast.error("finYearId missing! Please login again.");
      return;
    }

    try {
      setLoading(true);

      const res = await axiosWithFY.get("/api/halfdayprovision/branches", {
        params: tenantParams({ page, limit: recordsPerPage }),
      });

      if (!res.data?.status) {
        toast.error(res.data?.message || "Failed to load branches");
        return;
      }

      const list = res.data.data || [];
      const mapped = list.map((row, idx) => ({
        id: row.fld_branchid,
        sNo: (page - 1) * recordsPerPage + (idx + 1),
        branchName: row.fld_BranchName,
        branchCode: row.fld_Branchcode,
        address1: row.fld_Address1,
        address2: row.fld_Address2,
        isMain: row.fld_isMainBranch === true || row.fld_isMainBranch === 1,
        enabled: !!row.fld_halfdayprovision,
      }));

      setRecords(mapped);

      setCurrentPage(res.data.page || page);
      setTotalPages(res.data.totalPages || 1);
      setTotalRecords(res.data.total || 0);
    } catch (err) {
      console.error("halfday list error:", err);
      toast.error(err.response?.data?.message || "Failed to load branches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = (branchId, checked) => {
    setRecords((prev) => prev.map((r) => (r.id === branchId ? { ...r, enabled: checked } : r)));

    setDirtyMap((prev) => {
      const m = new Map(prev);
      m.set(branchId, checked);
      return m;
    });
  };

  const handleSave = async () => {
    if (!finYearId) {
      toast.error("finYearId missing! Please login again.");
      return;
    }

    const changes = Array.from(dirtyMap.entries()).map(([branchId, enabled]) => ({
      branchId,
      enabled,
    }));

    if (changes.length === 0) {
      toast.info("No changes to save");
      return;
    }

    try {
      const res = await axiosWithFY.post("/api/halfdayprovision/branches/save", {
        changes,
      });

      if (res.data?.status) {
        toast.success(res.data?.message || "Saved Successfully");
        setDirtyMap(new Map());
        await fetchList(currentPage);
      } else {
        toast.error(res.data?.message || "Failed");
      }
    } catch (err) {
      console.error("halfday save error:", err);
      toast.error(err.response?.data?.message || "Server error");
    }
  };

  const handlePageChange = (page) => {
    if (!page || page === "..." || page === currentPage) return;
    fetchList(page);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      for (let i = 1; i <= maxVisiblePages; i++) pages.push(i);
      pages.push("...");
    }
    return pages;
  };

  return (
    <AdminLayoutWrapper>
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">HALFDAY PROVISION - SELECT BRANCHES</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Branch Listing
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {totalRecords} Records
              </span>
              {dirtyMap.size > 0 && (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-yellow-900 text-yellow-100" : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {dirtyMap.size} Unsaved change(s)
                </span>
              )}
            </div>

            <button
              onClick={handleSave}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
            >
              <FaSave />
              Save
            </button>
          </div>

          {/* Table */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">S.No</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Branch Name</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Branch Code</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Address1</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Address2</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Is Main</th>
                    <th className="px-4 py-4 text-center font-semibold">Select</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className={`px-4 py-6 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        Loading...
                      </td>
                    </tr>
                  ) : records.length === 0 ? (
                    <tr>
                      <td colSpan="7" className={`px-4 py-6 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    records.map((r, idx) => (
                      <tr
                        key={r.id}
                        className={`transition-colors duration-200 ${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-gray-50"
                            : isDark
                            ? "bg-gray-900"
                            : "bg-white"
                        }`}
                      >
                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.sNo}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.branchName}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.branchCode}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.address1}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.address2}
                        </td>
                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.isMain ? "Yes" : "No"}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={!!r.enabled}
                            onChange={(e) => handleToggle(r.id, e.target.checked)}
                            className={`w-4 h-4 cursor-pointer ${isDark ? "accent-blue-400" : "accent-blue-600"}`}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center p-4">
              <div className={`${isDark ? "text-gray-300" : "text-gray-700"} text-sm font-medium`}>
                Total: {totalRecords} | Page: {currentPage} / {totalPages}
              </div>

              <div className="flex gap-1">
                {renderPageNumbers().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => handlePageChange(page)}
                    disabled={page === "..."}
                    className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                      page === currentPage
                        ? "bg-blue-600 text-white border-blue-600"
                        : page === "..."
                        ? isDark
                          ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                          : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                        : isDark
                        ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AdminLayoutWrapper>
  );
};

export default HalfdayProvisionSelectBranches;
